import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";

/**
 * Staff activation landing — invited pro-shop staff member arrives here from
 * a /staff/activate?token=XXX email link, sets a 6-12 digit personal back-
 * office passcode, and is logged into the back office immediately.
 */
export default function StaffActivatePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    if (!token) {
      setError("Missing invitation token.");
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get(`/public/staff-invitations/validate?token=${encodeURIComponent(token)}`);
      setInvite(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <PageShell eyebrow="Staff Activation" title="Validating your invitation…">
        <p data-testid="staff-activate-validating" className="text-[#4A4A4A] mt-3">One moment.</p>
      </PageShell>
    );
  }

  if (error || !invite) {
    return (
      <PageShell eyebrow="Staff Activation" title="Invitation unavailable">
        <div data-testid="staff-activate-error" className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error || "This staff invitation could not be found."}
        </div>
        <div className="mt-6">
          <Link to="/back-office/login" className="text-sm text-[#1A422D] underline underline-offset-4">
            Go to back-office sign in →
          </Link>
        </div>
      </PageShell>
    );
  }

  if (invite.status !== "pending") {
    return (
      <PageShell eyebrow="Staff Activation" title="This invitation can no longer be used">
        <div data-testid="staff-activate-status-banner" className="mt-6 px-4 py-3 border border-[#B59A5A] bg-[#F8F1DE] text-[#8a6f33] text-sm">
          {invite.message}
        </div>
        <p className="text-[#4A4A4A] mt-4 text-sm">
          Ask the Club Admin at <strong>{invite.club?.name}</strong> to resend you a fresh activation link.
        </p>
      </PageShell>
    );
  }

  return <ActivateForm
    invite={invite}
    token={token}
    onSuccess={(user) => {
      setUser(user);
      navigate("/admin", { replace: true });
    }}
  />;
}

function ActivateForm({ invite, token, onSuccess }) {
  const [passcode, setPasscode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [title, setTitle] = useState(invite?.title || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (passcode !== confirm) {
      setError("Both passcodes must match.");
      return;
    }
    if (!/^\d{6,12}$/.test(passcode)) {
      setError("Passcode must be 6-12 digits only.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/staff-invitations/accept", {
        token,
        passcode,
        title: title.trim() || undefined,
      });
      if (data?.user) onSuccess(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell eyebrow="Staff Activation" title={`Welcome to ${invite.club?.name}`}>
      <div data-testid="staff-activate-club-banner" className="mt-4 p-4 border border-[#E5E3D8] bg-[#FAF9F2] text-sm text-[#4A4A4A]">
        <div className="overline mb-1">Pro-Shop Activation</div>
        <div className="font-serif text-xl text-[#1A1A1A]">{invite.club?.name}</div>
        <div className="mt-2 text-xs text-[#7A7A7A]">
          Activating: <strong className="text-[#1A1A1A]">{invite.name_hint}</strong>
          {invite.title && <> · {invite.title}</>}
        </div>
        <div className="text-xs text-[#7A7A7A]">{invite.email}</div>
      </div>

      <p className="text-[#4A4A4A] mt-4 text-sm leading-snug">
        Choose a personal back-office passcode <strong>only you</strong> will use at the pro-shop terminal.
        Every action you take in the back office (member edits, order status changes, invitations sent,
        wishlist updates) will be stamped with your name in the audit log.
      </p>

      <ul className="mt-3 text-xs text-[#7A7A7A] space-y-1 list-disc pl-5">
        <li>6 to 12 digits, numbers only.</li>
        <li>No repeated digits (e.g. 111111) and no straight sequences (e.g. 123456).</li>
        <li>Don&rsquo;t reuse the club&rsquo;s shared passcode.</li>
      </ul>

      {error && (
        <div data-testid="staff-activate-form-error" className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-5">
        <div>
          <label className="label-elegant block mb-2">Personal passcode</label>
          <PasswordInput
            inputMode="numeric"
            pattern="[0-9]{6,12}"
            maxLength={12}
            minLength={6}
            required
            value={passcode}
            onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ""))}
            data-testid="staff-activate-passcode"
            className="input-field text-lg tracking-[0.4em]"
            placeholder="••••••"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">Confirm passcode</label>
          <PasswordInput
            inputMode="numeric"
            pattern="[0-9]{6,12}"
            maxLength={12}
            minLength={6}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
            data-testid="staff-activate-passcode-confirm"
            className="input-field text-lg tracking-[0.4em]"
            placeholder="••••••"
          />
        </div>
        <div>
          <label className="label-elegant block mb-2">Your role at the pro shop</label>
          <input
            type="text"
            maxLength={80}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="staff-activate-title"
            className="input-field"
            placeholder="e.g. Buyer, Head Pro, Assistant"
          />
          <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">Shown next to your name in the back office.</p>
        </div>
        <button
          type="submit"
          disabled={busy || passcode.length < 6 || confirm.length < 6}
          data-testid="staff-activate-submit"
          className="btn-primary"
        >
          {busy ? "Activating…" : "Activate & sign in to back office"}
        </button>
      </form>
    </PageShell>
  );
}

function PageShell({ eyebrow, title, children }) {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6 sm:p-12">
      <div data-testid="staff-activate-page" className="w-full max-w-xl surface-card p-8 sm:p-12 fade-up">
        <div className="overline">{eyebrow}</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">{title}</h1>
        {children}
      </div>
    </div>
  );
}
